import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error in React component tree:', error, errorInfo);
    (this as any).setState({ errorInfo });
  }

  public render() {
    const state = (this as any).state;
    const props = (this as any).props;
    if (state.hasError) {
      if (props.fallback) {
        return props.fallback;
      }
      return (
        <div style={{ padding: '2rem', color: 'red', backgroundColor: 'black', minHeight: '100vh' }}>
          <h1 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Something went wrong.</h1>
          <p style={{ marginBottom: '1rem' }}>
            We're sorry, but an error occurred while loading this page. This may be due to browser incompatibility or a temporary issue.
          </p>
          <pre style={{ background: '#222', padding: '1rem', overflow: 'auto', borderRadius: '4px' }}>
            <code>{state.error?.toString()}</code>
          </pre>
          {state.errorInfo && (
            <details style={{ whiteSpace: 'pre-wrap', marginTop: '1rem', color: '#ccc' }}>
              <summary>Error Details</summary>
              <br />
              {state.errorInfo.componentStack}
            </details>
          )}
        </div>
      );
    }

    return props.children;
  }
}
